import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  TASKS_SERVICE_URL,
  fetchAPI,
} from "@coworker/app/src/hooks/API/tasks.service.helper";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { getEnvironmentId } from "@coworker/reusable/helpers/environment";
import { useQuery } from "@tanstack/react-query";

type InsightsDataFilters = {
  itemNo?: string;
  refillAreaId?: string;
  itemRefillLocationGridCode?: string;
  itemHfbNo?: string;
  selectedPeriod: string;
};

export type InsightsTablesType =
  | "articleInsights"
  | "refilledAreaInsights"
  | "refilledSalesLocationIDInsights"
  | "itemHfbTypeInsights";

export type InsightsOrderByColumn =
  | "estimatedTurnover"
  | "pieces"
  | "numberOfRefillLocations"
  | "numberOfArticles";

export type InsightsOrderByValues = {
  column: InsightsOrderByColumn;
  direction: "asc" | "desc";
};

export type InsightsOrderByColumns = {
  [table in InsightsTablesType]: InsightsOrderByValues;
};

type InsightsTablePages = {
  [table in InsightsTablesType]: number;
};

type InsightsEndpoints =
  | "articles"
  | "refill_areas"
  | "refilled_sales_location_ids"
  | "item_hfb_types"
  | "total_counts";

const getInsightsEndpointUrl = (
  storeId: string,
  endpoint: InsightsEndpoints,
  filters: InsightsDataFilters,
  pageParam: number,
  orderByValue?: string
) => {
  const pageSize = 5;

  let url =
    endpoint === "total_counts"
      ? `insights/total_counts?storeId=${storeId}&`
      : `insights/${endpoint}?storeId=${storeId}&${orderByValue}&pageSize=${pageSize}&page=${pageParam}&`;

  for (const key in filters) {
    const value = filters[key as keyof InsightsDataFilters];

    if (!value) {
      continue;
    }

    if (value) {
      url += `${key}=${encodeURIComponent(value)}&`;
    }
  }

  return url;
};

// @google-cloud/bigquery approach is not supported in China, so we use direct API endpoints for Chinese users instead
const chinaInsightsEndpoint = () => {
  const environmentId = getEnvironmentId();
  if (environmentId === "dev") {
    return "https://api.dev.fixa.ingka.com/eu-w1/tasks-service";
  } else if (environmentId === "prod") {
    return "https://fixa-api.ingka.com/as-e1/tasks-service";
  } else {
    console.error(
      `Unknown environmentId '${environmentId}' in chinaInsightsEndpoint, defaulting to dev endpoint.`
    );
    return "https://api.dev.fixa.ingka.com/eu-w1/tasks-service";
  }
};

const getInsightsEndpoint = () => {
  return isChineseEnvironment() ? chinaInsightsEndpoint() : TASKS_SERVICE_URL;
};

const fetchInsights = async (
  storeId: string,
  endpoint: InsightsEndpoints,
  filters: InsightsDataFilters,
  pageParam: number,
  orderByColumn?: string
) => {
  const insightsEndpoint = getInsightsEndpoint();
  const callUrl = `${insightsEndpoint}/${getInsightsEndpointUrl(storeId, endpoint, filters, pageParam, orderByColumn)}`;
  const response = await fetchAPI(
    "GET",
    callUrl,
    {},
    {},
    {
      "X-Api-Key": import.meta.env["VITE_APP_INSIGHTS_API_KEY"] || "",
    }
  );
  const data = response.result;

  if (endpoint === "total_counts" && !!data) {
    return data;
  }

  if (data) {
    return { ...data, pageParam };
  }

  return { rows: [], totalRowCount: 0, pageParam };
};

export const useInsightsData = (
  tablePages: InsightsTablePages,
  filters: InsightsDataFilters,
  orderByColumn: InsightsOrderByColumns
) => {
  const storeId = useStoreId();
  const filtersIndex = [];
  for (const key in filters) {
    if (filters[key as keyof InsightsDataFilters]) {
      filtersIndex.push(`${key}=${filters[key as keyof InsightsDataFilters]}`);
    }
  }
  filtersIndex.sort();

  const orderByValue = (orderBy: {
    column: InsightsOrderByColumn;
    direction: "asc" | "desc";
  }) => {
    if (!orderBy) return "";
    return `&orderByColumn=${orderBy.column}&orderDirection=${orderBy.direction}`;
  };

  const articleInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "articleInsights",
      tablePages.articleInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.articleInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "articles",
        filters,
        tablePages.articleInsights,
        orderByValue(orderByColumn.articleInsights)
      );
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const refilledAreaInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "refilledAreaInsights",
      tablePages.refilledAreaInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.refilledAreaInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "refill_areas",
        filters,
        tablePages.refilledAreaInsights,
        orderByValue(orderByColumn.refilledAreaInsights)
      );
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const refilledSalesLocationIDInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "refilledSalesLocationIDInsights",
      tablePages.refilledSalesLocationIDInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.refilledSalesLocationIDInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "refilled_sales_location_ids",
        filters,
        tablePages.refilledSalesLocationIDInsights,
        orderByValue(orderByColumn.refilledSalesLocationIDInsights)
      );
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const itemHfbTypeInsightsQueryResult = useQuery({
    queryKey: [
      "insights",
      "itemHfbTypeInsights",
      tablePages.itemHfbTypeInsights,
      ...filtersIndex,
      orderByValue(orderByColumn.itemHfbTypeInsights),
    ],
    queryFn: async () => {
      return await fetchInsights(
        storeId,
        "item_hfb_types",
        filters,
        tablePages.itemHfbTypeInsights,
        orderByValue(orderByColumn.itemHfbTypeInsights)
      );
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const totalCounts = useQuery({
    queryKey: ["insights", "totalCounts", ...filtersIndex],
    queryFn: async () =>
      await fetchInsights(storeId, "total_counts", filters, 1),
  });

  return {
    articleInsights: articleInsightsQueryResult,
    refilledAreaInsights: refilledAreaInsightsQueryResult,
    refilledSalesLocationIDInsights: refilledSalesLocationIDInsightsQueryResult,
    itemHfbTypeInsights: itemHfbTypeInsightsQueryResult,
    totalCounts: totalCounts,
  };
};
